import { useMemo, useState } from 'react';
import { ControlPosition, useMap, Map } from 'shared/map-exports';
import { useTranslation } from 'react-i18next';

import MapToolsIcon from 'assets/map_tools.svg?react';
import {
  ContextMenuItemKey,
  MapToolsContextMenu,
} from 'features/mapping/components/MapToolsContextMenu';
import { LatLng } from 'shared/types';
import { useMapGLController } from 'features/mapping/hooks/useMapGLController/useMapGLController';
import { useLockedMapPin } from 'features/mapping/mapPin';
import { makeStyles } from 'tss-react/mui';
import { MapControlButton } from '../MapControlButton';
import {
  BaseMapboxGLController,
  BaseMapboxGLControllerProps,
} from '../BaseMapboxGLController';

type MapToolsControlProps = {
  position?: ControlPosition;
  exclude?: ContextMenuItemKey[];
};

type MapToolsControlContentProps = {
  map?: Map;
  onClick: (centerCoordinates: LatLng) => void;
};

const useStyles = makeStyles()((theme) => ({
  controlIcon: {
    'g > path': {
      fill: theme.palette.text.primary,
    },
    'g > circle': {
      fill: theme.palette.button.surface.dark,
    },
  },
}));

const MapToolsControlContent = (
  props: MapToolsControlContentProps & BaseMapboxGLControllerProps,
): JSX.Element => {
  const { map, onClick } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  return (
    <MapControlButton
      text={t('map.controls.tools')}
      Icon={MapToolsIcon}
      iconClassName={classes.controlIcon}
      onClick={() => {
        const center = map?.getCenter();
        if (center) {
          onClick(center);
        }
      }}
    />
  );
};

class MapboxGLMapToolsController extends BaseMapboxGLController<
  MapToolsControlContentProps & BaseMapboxGLControllerProps
> {
  content = (): JSX.Element => (
    <MapToolsControlContent {...this.props} map={this.map} />
  );
}

export const MapToolsControl = (props: MapToolsControlProps): JSX.Element => {
  const { position, exclude = ['copyPosition'] } = props;
  const [mapCenter, setMapCenter] = useState<LatLng | null>(null);
  const { current: map } = useMap();
  const { lockedPin } = useLockedMapPin();

  const contentProps = useMemo(
    () => ({
      map: map?.getMap(),
      onClick: (centerCoordinates: LatLng) => setMapCenter(centerCoordinates),
      position,
    }),
    [map, position],
  );

  useMapGLController(contentProps, MapboxGLMapToolsController);

  // When locked, the tools menu operates on the locked pin, not the map center.
  let pointCoordinates: LatLng | null = mapCenter;
  if (mapCenter && lockedPin) {
    pointCoordinates = lockedPin;
  }

  return (
    <MapToolsContextMenu
      variant="drawer"
      exclude={exclude}
      pointCoordinates={pointCoordinates}
      onClose={() => setMapCenter(null)}
    />
  );
};
